import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router-dom";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    ReportingTagBreadcrumbs,
    ReportingTagCategoryBreadcrumbs,
    ReportingTagCategoryCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as ReportingTagCategoryActions from "../../../actions/reportingTagCategories";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { QALookupsLayout } from "../../index";

import "./ReportingTagCategoryPage.scss";

interface ReportingTagCategoryPageProps {
    form: Dtos.ReportingTagCategory & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reportingTagCategory: Dtos.ReportingTagCategory;
    lookups: Dtos.Lookup[];
    loadingReportingTagCategory: boolean;
    loadReportingTagCategoryFailure: boolean;
    loadReportingTagCategoryErrorCode: string;
    savingReportingTagCategory: boolean;
    saveReportingTagCategorySuccess: boolean;
    saveReportingTagCategoryFailure: boolean;
    creatingReportingTagCategory: boolean;
    createReportingTagCategorySuccess: boolean;
    validatingReportingTagCategory: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearReportingTagCategory: ReportingTagCategoryActions.IReportingTagCategoryClearActionCreator;
    loadReportingTagCategory: ReportingTagCategoryActions.IReportingTagCategoryLoadByIdActionCreator;
    saveReportingTagCategory: ReportingTagCategoryActions.IReportingTagCategorySaveActionCreator;
    createReportingTagCategory: ReportingTagCategoryActions.IReportingTagCategoryCreateActionCreator;
    validateReportingTagCategory: ReportingTagCategoryActions.IReportingTagCategoryValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.reportingTagCategory";

const ReportingTagCategoryPage: FC<ReportingTagCategoryPageProps> = ({
    form,
    formState,
    formProperties,
    reportingTagCategory,
    lookups,
    loadingReportingTagCategory,
    loadReportingTagCategoryFailure,
    loadReportingTagCategoryErrorCode,
    savingReportingTagCategory,
    saveReportingTagCategorySuccess,
    saveReportingTagCategoryFailure,
    creatingReportingTagCategory,
    createReportingTagCategorySuccess,
    validatingReportingTagCategory,
    validationFailures,
    permissions,
    clearReportingTagCategory,
    loadReportingTagCategory,
    saveReportingTagCategory,
    createReportingTagCategory,
    validateReportingTagCategory,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { reportingTagCategoryId } = useParams<{
        reportingTagCategoryId: string;
    }>();
    const reportingTagCategoryIdNumber = parseInt(reportingTagCategoryId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.ReportingTagCategory =
            {} as Dtos.ReportingTagCategory;

        loadForm(
            reduxFormName,
            Object.assign({ ...reportingTagCategory }, extraData)
        );
    };


    // Load reporting tag category
    useEffect(() => {
        if (
            reportingTagCategoryIdNumber &&
            !reportingTagCategory &&
            !loadingReportingTagCategory
        ) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(
                ReportingTagCategoryBreadcrumbs(reportingTagCategoryIdNumber)
            );
            loadReportingTagCategory(reportingTagCategoryIdNumber);
        } else if (
            !reportingTagCategoryIdNumber &&
            !creatingReportingTagCategory &&
            !createReportingTagCategorySuccess
        ) {
            createReportingTagCategory();
            setBreadcrumbs(ReportingTagCategoryCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (reportingTagCategory && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        reportingTagCategoryIdNumber,
        reportingTagCategory,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        ReportingTagBreadcrumbs,
        loadReportingTagCategory,
        createReportingTagCategory,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingReportingTagCategory,
        creatingReportingTagCategory,
        createReportingTagCategorySuccess,
        ReportingTagCategoryCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadReportingTagCategoryFailure) {
            navigate(`/error/${loadReportingTagCategoryErrorCode}`);
        }
    }, [
        loadReportingTagCategoryFailure,
        loadReportingTagCategoryErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (reportingTagCategoryIdNumber && reportingTagCategory?.value) {
            setBreadcrumbs(
                ReportingTagBreadcrumbs(
                    reportingTagCategoryIdNumber,
                    reportingTagCategory?.value
                )
            );
        }
    }, [
        reportingTagCategoryIdNumber,
        reportingTagCategory,
        setBreadcrumbs,
        ReportingTagBreadcrumbs,
    ]);

    // Reload reporting tag after saving
    useEffect(() => {
        if (!savingReportingTagCategory && saveReportingTagCategorySuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Reporting tag category saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/qa/reportingtagcategories";
                navigate(url);
            } else if (!reportingTagCategoryIdNumber) {
                let url: string = `/settings/qa/reportingtagcategories/${reportingTagCategory?.id}`;
                navigate(url);
                clearReportingTagCategory();
            }
        }
    }, [
        savingReportingTagCategory,
        saveReportingTagCategorySuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingReportingTagCategory && saveReportingTagCategoryFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingReportingTagCategory, saveReportingTagCategoryFailure]);

    // Cleanup effect to clear the reporting tag category on component unmount
    useEffect(() => {
        return () => {
            clearReportingTagCategory();
            resetForm(reduxFormName);
        };
    }, [clearReportingTagCategory, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/qa/reportingtagcategories";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.ReportingTagCategory) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.ReportingTagCategory =
                {} as Dtos.ReportingTagCategory;

            await saveReportingTagCategory(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.ReportingTagCategory =
                {} as Dtos.ReportingTagCategory;

            if (form && hasBeenSubmitted && !validatingReportingTagCategory) {
                await validateReportingTagCategory(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.ReportingTagCategoriesUpdate,
                Dtos.Permission.ReportingTagCategoriesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingReportingTagCategory ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "reportingTagCategory",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "reportingTagCategory",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "reportingTagCategory",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "reportingTagCategory",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingReportingTagCategory,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <QALookupsLayout
            loading={loadingReportingTagCategory}
            permission={Dtos.Permission.ReportingTagCategoriesView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<ReportingTagCategoryPageProps> => {
    let reportingTagCategory: Dtos.ReportingTagCategory | undefined = !(
        state.reportingTagCategories.formData instanceof Array
    )
        ? state.reportingTagCategories.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        reportingTagCategory: reportingTagCategory!,
        loadingReportingTagCategory:
            state.reportingTagCategories.loadState &&
            state.reportingTagCategories.loadState.status ==
                RequestState.Pending,
        loadReportingTagCategoryFailure:
            state.reportingTagCategories.loadState &&
            state.reportingTagCategories.loadState.status ==
                RequestState.Failure,
        loadReportingTagCategoryErrorCode:
            state.reportingTagCategories.loadState.errorCode,
        savingReportingTagCategory:
            state.reportingTagCategories.saveState &&
            state.reportingTagCategories.saveState.status ==
                RequestState.Pending,
        saveReportingTagCategorySuccess:
            state.reportingTagCategories.saveState &&
            state.reportingTagCategories.saveState.status ==
                RequestState.Success,
        saveReportingTagCategoryFailure:
            state.reportingTagCategories.saveState &&
            state.reportingTagCategories.saveState.status ==
                RequestState.Failure,
        creatingReportingTagCategory:
            state.reportingTagCategories.createState &&
            state.reportingTagCategories.createState.status ==
                RequestState.Pending,
        createReportingTagCategorySuccess:
            state.reportingTagCategories.createState &&
            state.reportingTagCategories.createState.status ==
                RequestState.Success,
        validatingReportingTagCategory:
            state.reportingTagCategories.validateState &&
            state.reportingTagCategories.validateState.status ==
                RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.reportingTagCategory,
        formState: state.reportingTagCategories.formState,
        formProperties: state.reportingTagCategories.formProperties,
        validationFailures: reportingTagCategory
            ? state.reportingTagCategories.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<ReportingTagCategoryPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadReportingTagCategory: bindActionCreators(
            ReportingTagCategoryActions.LoadReportingTagCategoryById,
            dispatch
        ),
        clearReportingTagCategory: bindActionCreators(
            ReportingTagCategoryActions.Clear,
            dispatch
        ),
        saveReportingTagCategory: bindActionCreators(
            ReportingTagCategoryActions.SaveReportingTagCategory,
            dispatch
        ),
        createReportingTagCategory: bindActionCreators(
            ReportingTagCategoryActions.CreateReportingTagCategory,
            dispatch
        ),
        validateReportingTagCategory: bindActionCreators(
            ReportingTagCategoryActions.ValidateReportingTagCategory,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ReportingTagCategoryPage);
